<script setup lang="ts">
import type { SupportMeasureActivity } from '@/composables/supportMeasureEntry/supportMeasureActivity.utils';
import { useSupportMeasureActivityDisplay } from '@/composables/supportMeasureEntry/useSupportMeasureActivityDisplay';
import { computed, toRefs } from 'vue';

const props = defineProps<{
  activities: SupportMeasureActivity[];
  /** Student view: names this student and counts the others the same action touched. */
  selectedStudentId?: number | null;
}>();
const { activities } = toRefs(props);

const display = useSupportMeasureActivityDisplay();
const label = computed(() => display.compactLabel(activities.value, props.selectedStudentId));

// German list: "a", "a und b", "a, b und c". The comma rides along with the name so it hugs it,
// while "und" stays a separate normal-weight part.
const nameParts = computed(() =>
  label.value.names.map((name, index, names) => ({
    precededByConjunction: index > 0 && index === names.length - 1,
    text: index < names.length - 2 ? `${name},` : name,
  })),
);
</script>

<template>
  <!-- Outer spacing belongs to the timeline row, so this renders no padding of its own. -->
  <p class="flex flex-wrap gap-x-xs text-footer text-secondary leading-[170%]">
    <template v-for="(part, index) in nameParts" :key="index">
      <span v-if="part.precededByConjunction">{{ $t('general.and') }}</span>
      <span class="font-semibold">{{ part.text }}</span>
    </template>
    <span v-if="label.additional">{{ label.additional }}</span>
    <span>{{ label.verb }}</span>
  </p>
</template>
